import { cn } from '@/platform/lib/utils';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const VARIANTS = {
    success: {
        Icon: CheckCircleIcon,
        iconClass: 'text-green-400',
        borderClass: 'border-l-4 border-green-400',
    },
    error: {
        Icon: XCircleIcon,
        iconClass: 'text-red-400',
        borderClass: 'border-l-4 border-red-400',
    },
    warning: {
        Icon: ExclamationTriangleIcon,
        iconClass: 'text-yellow-400',
        borderClass: 'border-l-4 border-yellow-400',
    },
};

const VALIDATION_TOAST = {
    type: 'error',
    title: 'Niet opgeslagen',
    message: 'Controleer de gemarkeerde velden en probeer het opnieuw.',
};

// An explicit Inertia::flash toast wins; otherwise fall back to a general
// notification when the response came back with validation errors.
const resolveToast = page =>
    page?.flash?.toast ??
    (Object.keys(page?.props?.errors ?? {}).length > 0
        ? VALIDATION_TOAST
        : null);

const Notification = () => {
    const initialToast = resolveToast(usePage());
    const [toast, setToast] = useState(initialToast);
    const [show, setShow] = useState(Boolean(initialToast));

    // Drive visibility off the Inertia response events rather than prop content:
    // resubmitting a form yields identical errors, so a content-based check would
    // never re-trigger after a manual dismiss.
    useEffect(() => {
        const present = next => {
            if (next) {
                setToast(next);
                setShow(true);
            }
        };

        const removeSuccess = router.on('success', event =>
            present(event.detail.page?.flash?.toast)
        );
        const removeError = router.on('error', event =>
            present(
                Object.keys(event.detail.errors ?? {}).length > 0
                    ? VALIDATION_TOAST
                    : null
            )
        );

        return () => {
            removeSuccess();
            removeError();
        };
    }, []);

    const { Icon, iconClass, borderClass } =
        VARIANTS[toast?.type] ?? VARIANTS.success;

    return createPortal(
        <div
            aria-live='assertive'
            className='pointer-events-none fixed inset-0 z-[3007] flex items-end px-4 py-6 sm:items-start sm:p-6'
        >
            <div className='flex w-full flex-col items-center space-y-4 sm:items-end'>
                <Transition show={show && Boolean(toast)}>
                    <div
                        className={cn(
                            'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white ring-1 shadow-lg ring-black/5 transition',
                            borderClass,
                            'data-closed:opacity-0',
                            'data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0',
                            'data-leave:duration-100 data-leave:ease-in'
                        )}
                    >
                        <div className='p-4'>
                            <div className='flex items-start'>
                                <div className='shrink-0'>
                                    <Icon
                                        aria-hidden='true'
                                        className={cn('size-6', iconClass)}
                                    />
                                </div>
                                <div className='ml-3 w-0 flex-1 pt-0.5'>
                                    {toast?.title && (
                                        <p className='text-sm font-medium text-gray-900'>
                                            {toast.title}
                                        </p>
                                    )}
                                    {toast?.message && (
                                        <p
                                            className={cn(
                                                'text-sm text-gray-500',
                                                toast?.title && 'mt-1'
                                            )}
                                        >
                                            {toast.message}
                                        </p>
                                    )}
                                </div>
                                <div className='ml-4 flex shrink-0'>
                                    <button
                                        type='button'
                                        onClick={() => setShow(false)}
                                        className='focus:ring-primary-600 inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-offset-2 focus:outline-hidden'
                                    >
                                        <span className='sr-only'>Sluiten</span>
                                        <XMarkIcon
                                            aria-hidden='true'
                                            className='size-5'
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>,
        document.body
    );
};

export default Notification;
